import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    Modal,
    Form,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Label,
    Input,
    Button,
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
    createCategory,
    editCategory,
    deleteCategory,
    assignBazaarsToCategory,
    unassignBazaarFromCategory,
} from "../../api/categories";
import TableContainer from "../../Components/Common/TableContainer";
import DeleteModal from "../../Components/Common/DeleteModal";
import "react-toastify/dist/ReactToastify.css";
import { getCategoriesQuery, getBazaarsQuery } from "../../queries";
import { useTranslation } from "react-i18next";
import Loader from "../../Components/Common/Loader";

const Categories = () => {
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language === "rs" ? "ru" : i18n.language;
    const queryClient = useQueryClient();

    const [modal, setModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [category, setCategory] = useState(null);
    const [deleteModal, setDeleteModal] = useState(false);

    // Fetch Categories
    const { data: categoriesData } = useQuery({
        ...getCategoriesQuery({
            isAll: true,
        }),
    });

    // Fetch Bazaars for assignment
    const { data: bazaarsData } = useQuery({
        ...getBazaarsQuery({
            size: 999,
            page: 1,
        }),
    });

    // Helper to process categories into tree
    const processedCategories = useMemo(() => {
        if (!categoriesData?.data?.data) return [];
        const cats = categoriesData.data.data.map(c => ({ ...c })); // shallow copy
        const map = {};
        cats.forEach(c => map[c.id] = { ...c, children: [] });

        const roots = [];
        cats.forEach(c => {
            if (c.upperId && map[c.upperId]) {
                map[c.upperId].children.push(map[c.id]);
            } else {
                roots.push(map[c.id]);
            }
        });

        const flatten = (nodes, depth = 0) => {
            let result = [];
            nodes.forEach(node => {
                node.depth = depth;
                result.push(node);
                if (node.children && node.children.length > 0) {
                    result = result.concat(flatten(node.children, depth + 1));
                }
            });
            return result;
        };

        return flatten(roots);
    }, [categoriesData]);

    const toggle = useCallback(() => {
        if (modal) {
            setModal(false);
            setCategory(null);
        } else {
            setModal(true);
        }
    }, [modal]);

    const onDelete = () => {
        deleteCategory(deleteModal)
            .then(() => {
                queryClient.invalidateQueries({ queryKey: ["categories"] });
                setDeleteModal(false);
                toast.success(t("Category deleted successfully"));
            })
            .catch((err) => {
                toast.error(err?.response?.data?.error?.message || t("Error deleting category"));
                setDeleteModal(false);
            });
    };

    const schema = Yup.object({
        names: Yup.array()
            .of(
                Yup.object({
                    localeKey: Yup.string().required(),
                    name: Yup.string().required(t("Name is required")),
                })
            )
            .length(3),
        // bazaarIds is mostly for create
    });

    const initialNames = useMemo(() => {
        const locales = category?.locales || [];
        const keys = ["uz", "ru", "uzc"];
        return keys.map(key => {
            const found = locales.find(l => l.localeKey === key);
            return { localeKey: key, name: found ? found.name : "" };
        });
    }, [category]);

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            upperId: (category && category.upperId) || "",
            names: initialNames,
            bazaarIds: (category && category.bazaarsCategories?.map(bc => bc.bazaarId)) || [],
        },
        validationSchema: schema,
        onSubmit: (values, { resetForm }) => {
            if (isEdit) {
                const initialBazaarIds = (category && category.bazaarsCategories?.map(bc => String(bc.bazaarId))) || [];
                const currentBazaarIds = values.bazaarIds.map(String);

                // Sort for comparison
                const sortedInitial = [...initialBazaarIds].sort();
                const sortedCurrent = [...currentBazaarIds].sort();
                const bazaarsChanged = JSON.stringify(sortedInitial) !== JSON.stringify(sortedCurrent);

                // Check other fields
                const nameInitial = JSON.stringify(validation.initialValues.names);
                const nameCurrent = JSON.stringify(values.names);
                const namesChanged = nameInitial !== nameCurrent;
                const upperIdChanged = validation.initialValues.upperId != values.upperId;

                const otherFieldsChanged = namesChanged || upperIdChanged;

                if (!bazaarsChanged && !otherFieldsChanged) {
                    toast.info(t("No changes detected"));
                    toggle();
                    return;
                }

                const promises = [];

                if (otherFieldsChanged) {
                    const payload = {
                        upperId: values.upperId || null,
                        names: values.names,
                        // Not including bazaarsCategories here as we use the dedicated API if they changed
                    };
                    promises.push(editCategory({ ...payload, id: category.id }));
                }

                if (bazaarsChanged) {
                    const added = currentBazaarIds.filter(id => !initialBazaarIds.includes(id));
                    const deleted = initialBazaarIds.filter(id => !currentBazaarIds.includes(id));

                    if (added.length > 0) {
                        promises.push(assignBazaarsToCategory(category.id, added.map(Number)));
                    }

                    deleted.forEach(bId => {
                        promises.push(unassignBazaarFromCategory(category.id, Number(bId)));
                    });
                }

                Promise.all(promises)
                    .then(() => {
                        queryClient.invalidateQueries({ queryKey: ["categories"] });
                        resetForm();
                        toggle();
                        toast.success(t("Category updated successfully"));
                    })
                    .catch((err) => {
                        console.error(err);
                        toast.error(t("Error updating category"));
                    });
            } else {
                const payload = {
                    upperId: values.upperId || null,
                    names: values.names,
                    bazaarsCategories: values.bazaarIds.map(id => ({ bazaarId: Number(id) })),
                };

                createCategory(payload)
                    .then(() => {
                        queryClient.invalidateQueries({ queryKey: ["categories"] });
                        resetForm();
                        toggle();
                        toast.success(t("Category created successfully"));
                    })
                    .catch((err) => {
                        console.error(err);
                        toast.error(t("Error creating category"));
                    });
            }
        },
    });

    const columns = useMemo(
        () => [
            {
                header: t("Name"),
                cell: ({ row }) => {
                    const depth = row.original.depth || 0;
                    const style = { paddingLeft: `${depth * 20}px` };
                    const ruName = row.original.locales?.find(l => l.localeKey === currentLang)?.name;
                    const name = ruName || row.original.locales?.[0]?.name || t("Unknown");

                    return (
                        <div style={style}>
                            {depth > 0 && <span className="text-muted me-1">└─</span>}
                            {name}
                        </div>
                    );
                },
            },
            {
                header: t("Actions"),
                id: "actions",
                cell: ({ row }) => (
                    <div className="d-flex gap-2">
                        <button
                            className="btn btn-sm btn-primary"
                            onClick={() => {
                                setCategory(row.original);
                                setIsEdit(true);
                                toggle();
                            }}
                        >
                            <i className="ri-edit-fill"></i>
                        </button>
                        <button
                            className="btn btn-sm btn-danger"
                            onClick={() => setDeleteModal(row.original.id)}
                        >
                            <i className="ri-delete-bin-6-fill"></i>
                        </button>
                    </div>
                ),
            },
        ],
        [toggle, t, currentLang]
    );

    document.title = "Categories | Admin";

    return (
        <div className="page-content">
            <DeleteModal
                show={!!deleteModal}
                onDeleteClick={onDelete}
                onCloseClick={() => setDeleteModal(false)}
            />

            <Container fluid>
                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="border-0 ">
                                <Row className="align-items-center gy-3">
                                    <div className="col-sm">
                                        <h5 className="mb-0 fw-bold">{t("Categories")}</h5>
                                    </div>
                                    <div className="col-sm-auto">
                                        <div className="d-flex gap-1 flex-wrap">
                                            <button
                                                type="button"
                                                className="btn btn-success add-btn"
                                                onClick={() => {
                                                    setIsEdit(false);
                                                    setCategory(null);
                                                    validation.resetForm();
                                                    toggle();
                                                }}
                                            >
                                                <i className="ri-add-line align-bottom me-1"></i>{" "}
                                                {t("create")}
                                            </button>
                                        </div>
                                    </div>
                                </Row>
                            </CardHeader>
                            <div className="py-2"></div>
                            <div className="card-body pt-0">
                                {processedCategories.length > 0 ? (
                                    <TableContainer
                                        columns={columns}
                                        data={processedCategories}
                                        isAddUserList={false}
                                        customPageSize={processedCategories.length}
                                        divClass="table-responsive table-card mb-3"
                                        tableClass="align-middle table-nowrap mb-0"
                                        theadClass="table-light table-nowrap"
                                        thClass="text-muted"
                                    />
                                ) : (
                                    <div className="py-4 text-center">
                                        <div>
                                            <lord-icon
                                                src="https://cdn.lordicon.com/msoeawqm.json"
                                                trigger="loop"
                                                colors="primary:#405189,secondary:#0ab39c"
                                                style={{ width: "72px", height: "72px" }}
                                            ></lord-icon>
                                        </div>
                                        <div className="mt-4">
                                            <h5>{t("No Result Found")}</h5>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Card>
                    </Col>
                </Row>
            </Container>

            <ToastContainer closeButton={false} limit={1} />

            <Modal isOpen={modal} toggle={() => {
                validation.resetForm();
                toggle();
            }} centered>
                <ModalHeader className="bg-light p-3" toggle={() => {
                    validation.resetForm();
                    toggle();
                }}>
                    {!!isEdit ? t("Edit Category") : t("Create Category")}
                </ModalHeader>
                <Form onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                    return false;
                }}>
                    <ModalBody>
                        <div className="mb-3">
                            <Label htmlFor="upperId" className="form-label">{t("Parent Category")}</Label>
                            <Input
                                type="select"
                                name="upperId"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.upperId}
                            >
                                <option value="">{t("None (Root)")}</option>
                                {processedCategories
                                    .filter(c => c.id !== category?.id) // Prevent self-parenting
                                    .map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {/* Simple indent in dropdown */}
                                            {'\u00A0'.repeat(c.depth * 4) + (c.locales?.find(l => l.localeKey === currentLang)?.name || c.locales?.[0]?.name)}
                                        </option>
                                    ))}
                            </Input>
                        </div>

                        {["uz", "ru", "uzc"].map((key, index) => (
                            <div className="mb-3" key={key}>
                                <Label className="form-label">{t("Name")} ({key})</Label>
                                <Input
                                    name={`names[${index}].name`}
                                    placeholder={`${t("Name")} (${key})`}
                                    value={validation.values.names[index].name}
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    invalid={
                                        validation.touched.names?.[index]?.name &&
                                        validation.errors.names?.[index]?.name
                                    }
                                />
                                {validation.touched.names?.[index]?.name && validation.errors.names?.[index]?.name && (
                                    <div className="text-danger">{validation.errors.names[index].name}</div>
                                )}
                            </div>
                        ))}

                        <div className="mb-3">
                            <Label htmlFor="bazaarIds" className="form-label">{t("Bazaars")}</Label>
                            <Input
                                type="select"
                                name="bazaarIds"
                                multiple
                                onChange={(e) => {
                                    const options = e.target.options;
                                    const value = [];
                                    for (let i = 0, l = options.length; i < l; i++) {
                                        if (options[i].selected) {
                                            value.push(options[i].value);
                                        }
                                    }
                                    validation.setFieldValue("bazaarIds", value);
                                }}
                                onBlur={validation.handleBlur}
                                value={validation.values.bazaarIds}
                                style={{ height: "150px" }}
                            >
                                {bazaarsData?.data?.data?.map((b) => (
                                    <option key={b.id} value={b.id}>
                                        {b.locales?.find(l => l.localeKey === currentLang)?.title || b.locales?.[0]?.title}
                                    </option>
                                ))}
                            </Input>
                            <div className="form-text">{t("Hold Ctrl (Windows) or Command (Mac) to select multiple options")}</div>
                        </div>

                    </ModalBody>
                    <ModalFooter>
                        <Button color="light" onClick={() => {
                            validation.resetForm();
                            toggle();
                        }}>{t("Close")}</Button>
                        <Button color="success" type="submit">{isEdit ? t("Save") : t("Create")}</Button>
                    </ModalFooter>
                </Form>
            </Modal>
        </div >
    );
};

export default Categories;
